import React, { useState, useEffect, useCallback } from 'react';
import { ADMIN_ORGANIZATION_ID } from './config/api';
import { hasAdminAccess } from './utils/auth';
import {
  Box,
  AppBar,
  Toolbar,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  Grid,
  Card,
  CardContent,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  AlertTitle,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  Tooltip,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  DeviceHub as DeviceIcon,
  Group as GroupIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Assessment as AssessmentIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  SignalCellular4Bar as CellularIcon,
  SmartToy as SmartToyIcon,
  Timeline as TimelineIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  DirectionsCar as DirectionsCarIcon,
  Code as CodeIcon,
  Apps as AppsIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Subscriptions as SubscriptionsIcon,
  Description as DescriptionIcon,
  Analytics as AnalyticsIcon,
  CreditCard as BillingIcon
} from '@mui/icons-material';
import { LinearProgress } from '@mui/material';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import PerformanceTab from './components/PerformanceTab.js';
import ReportTab from './components/ReportTab.js';
import CellularTab from './components/CellularTab.js';
import AgentTab from './components/AgentTab.js';
import TelemetryTab from './components/TelemetryTab.js';
import organizationRoleService from './services/organizationRoleService.js';
import OrganizationRoleManagement from './components/OrganizationRoleManagement.js';
import ApiDocumentation from './components/ApiDocumentation.js';
import DocumentationTab from './components/DocumentationTab.js';
import ApplicationsTab from './components/ApplicationsTab.js';
import SubscriptionTab from './components/SubscriptionTab.js';
import VisionTab from './components/VisionTab.js';
import ApiUsageTab from './components/ApiUsageTab.js';
import BillingTab from './components/BillingTab.js';
import PerformanceDashboard from './components/PerformanceDashboard.js';
import { fetchDevicePictures, fetchDevicePerformanceData, fetchDeviceReportData } from './services/apiService.js';
import SimpleConnectivityTable from './components/performance/SimpleConnectivityTable.js';
import SimplePayloadTable from './components/performance/SimplePayloadTable.js';
import SimpleBatteryTable from './components/performance/SimpleBatteryTable.js';
import DevicePerformanceTable from './components/DevicePerformanceTable.js';
import { API_BASE_URL, API_ENDPOINTS } from './config/api.js';
import { WalteroThemeProvider, WalteroDataTable, WalteroTypography, WalteroButton, WalteroCard } from './components/waltero';
import { hasOrganizationAccess } from './utils/auth';
import { formatDateInTimezone, getUserTimezone } from './utils/timezoneUtils';
import WalteroLandingPage from './components/WalteroLandingPage';

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// SVG-based colored markers - no CSS needed

// Use industry standard Leaflet icons with custom colors
const greenPin = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 19.4 12.5 41 12.5 41C12.5 41 25 19.4 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="#4CAF50" stroke="white" stroke-width="2"/>
      <circle cx="12.5" cy="12.5" r="4" fill="white"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

const bluePin = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 19.4 12.5 41 12.5 41C12.5 41 25 19.4 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="#2196F3" stroke="white" stroke-width="2"/>
      <circle cx="12.5" cy="12.5" r="4" fill="white"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

// Configure axios to include auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    console.log(`🌐 Making request to: ${config.method?.toUpperCase()} ${config.url}`);
    console.log(`🔑 Token in localStorage: ${token ? 'Present' : 'Missing'}`);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`✅ Authorization header set`);
    } else {
      console.log(`❌ No token found, request will be unauthenticated`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only reload if this is NOT a login request
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      if (!isLoginRequest) {
        localStorage.removeItem('authToken');
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);


const drawerWidth = 240;
const drawerWidthClosed = 64;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, value: 'dashboard' },
  { text: 'Users', icon: <PeopleIcon />, value: 'users' },
  { text: 'Organizations', icon: <BusinessIcon />, value: 'organizations' },
  { text: 'Applications', icon: <AppsIcon />, value: 'applications' },
  { text: 'Devices', icon: <DeviceIcon />, value: 'devices' },
  { text: 'Roles', icon: <GroupIcon />, value: 'groups' },
  { text: 'Fleet', icon: <DirectionsCarIcon />, value: 'performance' },
  { text: 'Consumption', icon: <AssessmentIcon />, value: 'report' },
  { text: 'Vision', icon: <PhotoLibraryIcon />, value: 'vision' },
  { text: 'Cellular', icon: <CellularIcon />, value: 'cellular' },
  { text: 'Telemetry', icon: <TimelineIcon />, value: 'telemetry' },
  { text: 'Agent', icon: <SmartToyIcon />, value: 'agent' },
  { text: 'API Docs', icon: <CodeIcon />, value: 'api-docs' },
  { text: 'API Usage', icon: <AnalyticsIcon />, value: 'api-usage' },
  { text: 'Documentation', icon: <DescriptionIcon />, value: 'documentation' },
  { text: 'Subscriptions', icon: <SubscriptionsIcon />, value: 'subscriptions' },
  { text: 'Billing', icon: <BillingIcon />, value: 'billing' },
];

const menuSections = [
  { title: null, items: ['dashboard'] },
  { title: 'Management', items: ['users', 'organizations', 'devices', 'groups', 'subscriptions', 'billing', 'applications'] },
  { title: 'Apps', items: ['report', 'vision'] },
  { title: 'Monitor', items: ['performance', 'cellular', 'telemetry', 'api-usage'] },
  { title: 'Support', items: ['api-docs', 'documentation', 'agent'] },
];

// Date calculation helper functions
const getDateString = (date) => {
  // Use local date to avoid timezone issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLastMonthDates = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0-based (0 = January, 7 = August)
  
  // Get first day of last month
  const lastMonthStart = new Date(currentYear, currentMonth - 1, 1);
  
  // Get last day of last month
  // Using day 0 of current month gives us the last day of previous month
  const lastMonthEnd = new Date(currentYear, currentMonth, 0);
  
  return {
    startDate: getDateString(lastMonthStart),
    endDate: getDateString(lastMonthEnd)
  };
};

const getThisMonthDates = () => {
  const today = new Date();
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    startDate: getDateString(thisMonthStart),
    endDate: getDateString(today)
  };
};

const getOneMonthDates = () => {
  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  return {
    startDate: getDateString(oneMonthAgo),
    endDate: getDateString(today)
  };
};

const getOneWeekDates = () => {
  const today = new Date();
  const oneWeekAgo = new Date(today);
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return {
    startDate: getDateString(oneWeekAgo),
    endDate: getDateString(today)
  };
};

// Helper function to format last login date in user's timezone
const formatLastLogin = (lastLoginDate) => {
  if (!lastLoginDate) return 'Never';
  
  try {
    const userTimezone = getUserTimezone();
    const date = new Date(lastLoginDate);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    // Format with user-friendly date/time format
    const formattedDate = formatDateInTimezone(
      date, 
      userTimezone, 
      'MMM dd, yyyy \'at\' h:mm a' // e.g., "Sep 18, 2024 at 6:01 PM"
    );
    
    return formattedDate;
  } catch (error) {
    console.error('Error formatting last login date:', error);
    return lastLoginDate; // Fallback to original format
  }
};

function App() {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [data, setData] = useState({ data: [], pagination: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemDetails, setItemDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [deviceTabValue, setDeviceTabValue] = useState(0);
  const [devicePerformanceData, setDevicePerformanceData] = useState(null);
  const [deviceReportData, setDeviceReportData] = useState(null);
  const [loadingDevicePerformance, setLoadingDevicePerformance] = useState(false);
  const [loadingDeviceReport, setLoadingDeviceReport] = useState(false);
  const [devicePerformanceStartDate, setDevicePerformanceStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [devicePerformanceEndDate, setDevicePerformanceEndDate] = useState(new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [editData, setEditData] = useState({});
  const [updating, setUpdating] = useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);
  const [bulkUpdateProgress, setBulkUpdateProgress] = useState({ current: 0, total: 0 });
  const [bulkUpdateResults, setBulkUpdateResults] = useState({ success: 0, failed: 0, errors: [] });
  const [bulkUpdatePreview, setBulkUpdatePreview] = useState(null);
  const [bulkUpdateStep, setBulkUpdateStep] = useState('upload'); // 'upload', 'preview', 'processing'
  const [stats, setStats] = useState({});
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState('');
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [organizationRoles, setOrganizationRoles] = useState({});
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [roleManagementOpen, setRoleManagementOpen] = useState(false);
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  
  // Device users state
  const [deviceUsers, setDeviceUsers] = useState([]);
  const [loadingDeviceUsers, setLoadingDeviceUsers] = useState(false);

  // Organization devices state
  const [orgDevices, setOrgDevices] = useState([]);
  const [loadingOrgDevices, setLoadingOrgDevices] = useState(false);
  const [orgDevicesPage, setOrgDevicesPage] = useState(0);
  const [orgDevicesRowsPerPage, setOrgDevicesRowsPerPage] = useState(10);

  // Fleet state
  const [performanceStartDate, setPerformanceStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [performanceEndDate, setPerformanceEndDate] = useState(new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [performanceData, setPerformanceData] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceError, setPerformanceError] = useState('');

  // Consumption state
  const [reportStartDate, setReportStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [reportEndDate, setReportEndDate] = useState(new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [reportData, setReportData] = useState(null);

  const [visionStartDate, setVisionStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [visionEndDate, setVisionEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [visionRefreshKey, setVisionRefreshKey] = useState(0);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');

  const sectionEndpoints = {
    users: 'users',
    organizations: 'organizations', 
    devices: 'devices',
    groups: 'groups',
    performance: 'organizations'
  };

  // Authentication functions
  const handleLogin = async (credentials) => {
    // Handle both event-based calls (from forms) and direct calls (from components)
    let loginData = credentials;
    if (credentials && typeof credentials.preventDefault === 'function') {
      // This is an event, use the existing loginForm state
      credentials.preventDefault();
      loginData = loginForm;
    }
    
    setLoginLoading(true);
    setLoginError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, loginData);
      const { token: authToken, user: userData } = response.data;
      
      localStorage.setItem('authToken', authToken);
      setToken(authToken);
      setUser(userData);
      setIsAuthenticated(true);
      setLoginOpen(false);
      setLoginForm({ username: '', password: '' });
      
      // Fetch initial data
      fetchStats();
      fetchOrganizations();
      
      return true; // Login successful
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.error || 'Login failed';
      setLoginError(errorMessage);
      return false; // Login failed
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setData({ data: [], pagination: {} });
    setStats({});
    setOrganizations([]);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Fetch current user data
  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
    } catch (error) {
      console.error('Error fetching current user:', error);
      // If the token is invalid, clear it
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  }, [token]);

  // Check if user is authenticated on mount
  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
      fetchCurrentUser();
      fetchStats();
      fetchOrganizations();
    }
  }, [token, fetchCurrentUser]);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch organization roles for admin users
  const fetchOrganizationRoles = useCallback(async (organizationIds) => {
    if (!hasAdminAccess() || !organizationIds || organizationIds.length === 0) {
      return;
    }

    try {
      const rolePromises = organizationIds.map(async (orgId) => {
        try {
          const roles = await organizationRoleService.getOrganizationRoles(orgId);
          return { orgId, roles: roles.data };
        } catch (error) {
          console.error(`Error fetching roles for organization ${orgId}:`, error);
          return { orgId, roles: { accountManagers: [], supportResponsible: [] } };
        }
      });

      const roleResults = await Promise.all(rolePromises);
      const rolesMap = {};
      roleResults.forEach(({ orgId, roles }) => {
        rolesMap[orgId] = roles;
      });
      setOrganizationRoles(rolesMap);
    } catch (error) {
      console.error('Error fetching organization roles:', error);
    }
  }, []);

  // Fetch organizations for filter
  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/organizations?limit=1000`);
      setOrganizations(response.data.data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  }, []);

  // Search organizations for autocomplete
  const searchOrganizations = useCallback(async (searchTerm) => {
    try {
      if (searchTerm === '') {
        // Use the main organizations endpoint for all organizations
        const response = await axios.get(`${API_BASE_URL}/organizations?limit=1000`);
        setOrganizationOptions(response.data.data);
      } else {
        // Use the search endpoint for filtered results
        const response = await axios.get(`${API_BASE_URL}/organizations/search?q=${encodeURIComponent(searchTerm)}`);
        setOrganizationOptions(response.data);
      }
    } catch (error) {
      console.error('Error searching organizations:', error);
      setOrganizationOptions([]);
    }
  }, []);

  // Fetch data based on active section
  const fetchData = useCallback(async () => {
    if (activeSection === 'dashboard' || activeSection === 'performance' || activeSection === 'report' || activeSection === 'vision' || activeSection === 'cellular' || activeSection === 'telemetry' || activeSection === 'applications' || activeSection === 'subscriptions' || activeSection === 'billing' || activeSection === 'agent' || activeSection === 'api-docs') return;
    
    try {
      setLoading(true);
      setError('');
      
      const endpoint = sectionEndpoints[activeSection];
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...(searchTerm && { search: searchTerm })
      };

      // Add organization filter for users and devices
      if ((activeSection === 'users' || activeSection === 'devices') && selectedOrganization) {
        params.organizationId = selectedOrganization;
      }

      // Add status filter for devices
      if (activeSection === 'devices') {
        params.status = 'active'; // Default to active devices
      }

      const response = await axios.get(`${API_BASE_URL}/${endpoint}`, { params });
      setData(response.data);
    } catch (error) {
      setError(`Failed to fetch ${activeSection}`);
      console.error(`Error fetching ${activeSection}:`, error);
    } finally {
      setLoading(false);
    }
  }, [activeSection, page, rowsPerPage, searchTerm, selectedOrganization]);

  // Fetch organization devices
  const fetchOrganizationDevices = useCallback(async (organizationId) => {
    try {
      setLoadingOrgDevices(true);
      const response = await axios.get(`${API_BASE_URL}/devices`, {
        params: {
          organizationId: organizationId,
          page: 1,
          limit: 100, // Get more devices for the map view
          status: 'active'
        }
      });
      setOrgDevices(response.data.data || []);
    } catch (error) {
      console.error('Error fetching organization devices:', error);
      setOrgDevices([]);
    } finally {
      setLoadingOrgDevices(false);
    }
  }, []);

  // Fetch item details
  const fetchItemDetails = useCallback(async (id, section = null) => {
    try {
      setLoadingDetails(true);
      const sectionToUse = section || activeSection;
      const endpoint = sectionEndpoints[sectionToUse];
      const response = await axios.get(`${API_BASE_URL}/${endpoint}/${id}`);
      setItemDetails(response.data);
      
      // If this is a device, also fetch users who have access to it
      if (sectionToUse === 'devices') {
        setLoadingDeviceUsers(true);
        try {
          const usersResponse = await axios.get(`${API_BASE_URL}/devices/${id}/users`);
          setDeviceUsers(usersResponse.data.users || []);
        } catch (error) {
          console.error('Error fetching device users:', error);
          setDeviceUsers([]);
        } finally {
          setLoadingDeviceUsers(false);
        }
      }
      
      // If this is an organization, also fetch its devices
      if (sectionToUse === 'organizations') {
        fetchOrganizationDevices(id);
      }
    } catch (error) {
      console.error('Error fetching item details:', error);
      setItemDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  }, [activeSection]);

  // Handle section change
  const handleSectionChange = (section) => {
    setActiveSection(section);
    setPage(0);
    setSearchTerm('');
    setSelectedOrganization('');
    setModalOpen(false);
    setSelectedItem(null);
    setItemDetails(null);
    setDeviceUsers([]);
    setLoadingDeviceUsers(false);
    // Clear fleet and report data when switching sections
    setPerformanceData(null);
    setPerformanceError('');
    setReportData(null);
    setReportError('');
    
    // Fetch organization roles only when switching to organizations section
    // Note: This will be handled by the useEffect that watches data changes
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Handle pagination
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle modal
  const handleOpenModal = (item) => {
    setSelectedItem(item);
    setModalOpen(true);
    fetchItemDetails(item.id);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
    setItemDetails(null);
    setEditMode(false);
    setEditData({});
    setDeviceUsers([]);
    setLoadingDeviceUsers(false);
    setOrgDevices([]);
    setLoadingOrgDevices(false);
    setOrgDevicesPage(0);
    setDeviceTabValue(0);
    setDevicePerformanceData(null);
    setDeviceReportData(null);
  };

  // Handle device tab change
  const handleDeviceTabChange = (event, newValue) => {
    setDeviceTabValue(newValue);
    
    // Load data based on tab selection
    if (newValue === 1 && !devicePerformanceData && itemDetails?.id) {
      loadDevicePerformanceData(itemDetails.id);
    } else if (newValue === 2 && !deviceReportData && itemDetails?.id) {
      loadDeviceReportData(itemDetails.id);
    }
  };

  // Load device performance data with custom date range
  const loadDevicePerformanceData = async (deviceId, customStartDate = null, customEndDate = null) => {
    if (loadingDevicePerformance) return;
    
    setLoadingDevicePerformance(true);
    try {
      const startDate = customStartDate || devicePerformanceStartDate;
      const endDate = customEndDate || devicePerformanceEndDate;
      const data = await fetchDevicePerformanceData(deviceId, startDate, endDate);
      setDevicePerformanceData(data);
    } catch (error) {
      console.error('Error loading device performance data:', error);
      setDevicePerformanceData({ error: 'Failed to load performance data' });
    } finally {
      setLoadingDevicePerformance(false);
    }
  };

  // Handle device performance date range change
  const handleDevicePerformanceDateChange = () => {
    if (itemDetails?.id) {
      loadDevicePerformanceData(itemDetails.id, devicePerformanceStartDate, devicePerformanceEndDate);
    }
  };

  // Load device report data  
  const loadDeviceReportData = async (deviceId) => {
    if (loadingDeviceReport) return;
    
    setLoadingDeviceReport(true);
    try {
      const { startDate, endDate } = getLastMonthDates();
      const data = await fetchDeviceReportData(deviceId, startDate, endDate, 'daily');
      setDeviceReportData(data);
    } catch (error) {
      console.error('Error loading device report data:', error);
      setDeviceReportData({ error: 'Failed to load report data' });
    } finally {
      setLoadingDeviceReport(false);
    }
  };

  // Handle user click from organization details
  const handleUserClick = (user) => {
    // Close current modal first
    handleCloseModal();
    
    // Switch to users tab
    setActiveSection('users');
    setPage(0);
    setSearchTerm('');
    setSelectedOrganization('');
    
    // Wait a bit for the tab switch, then open the user modal
    setTimeout(() => {
      // Create user object in the format expected by handleOpenModal
      const userForModal = {
        id: user.id,
        firstname: user.firstname,
        lastname: user.lastname,
        ...user
      };
      
      // Open the modal and fetch details using the users endpoint
      setSelectedItem(userForModal);
      setModalOpen(true);
      fetchItemDetails(userForModal.id, 'users');
    }, 100);
  };

  // Handle organization click from user details
  const handleOrganizationClick = (organization) => {
    // Close current modal first
    handleCloseModal();
    
    // Switch to organizations tab
    setActiveSection('organizations');
    setPage(0);
    setSearchTerm('');
    setSelectedOrganization('');
    
    // Wait a bit for the tab switch, then open the organization modal
    setTimeout(() => {
      // Create organization object in the format expected by handleOpenModal
      const orgForModal = {
        id: organization.organization_id || organization.id,
        name: organization.name || organization.organization_name,
        ...organization
      };
      
      // Open the modal and fetch details using the organizations endpoint
      setSelectedItem(orgForModal);
      setModalOpen(true);
      fetchItemDetails(orgForModal.id, 'organizations');
    }, 100);
  };

  // Handle edit mode
  const handleEditMode = () => {
    if (itemDetails && activeSection === 'devices') {
      setEditData({
        name: itemDetails.name || '',
        externalmeterid: itemDetails.externalmeterid || '',
        description: itemDetails.description || '',
        isactivated: itemDetails.isactivated,
        latitude: itemDetails.latitude || 0,
        longitude: itemDetails.longitude || 0
      });
      setEditMode(true);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditData({});
  };

  const handleUpdateDevice = async () => {
    if (!itemDetails || activeSection !== 'devices') return;
    
    setUpdating(true);
    try {
      const response = await axios.put(`${API_BASE_URL}/devices/${itemDetails.id}`, editData);
      
      // Fetch the complete device details again to get all fields
      const deviceResponse = await axios.get(`${API_BASE_URL}/devices/${itemDetails.id}`);
      
      // Update the item details with the complete device data
      setItemDetails(deviceResponse.data);
      setEditMode(false);
      setEditData({});
      
      // Refresh the data list to show updated information
      fetchData();
    } catch (error) {
      console.error('Error updating device:', error);
      setError(error.response?.data?.error || 'Failed to update device');
    } finally {
      setUpdating(false);
    }
  };

  const handleEditDataChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Bulk update functions
  const handleBulkUpdateOpen = () => {
    setBulkUpdateOpen(true);
    setBulkUpdateResults({ success: 0, failed: 0, errors: [] });
    setBulkUpdateProgress({ current: 0, total: 0 });
    setBulkUpdatePreview(null);
    setBulkUpdateStep('upload');
  };

  const handleBulkUpdateClose = () => {
    setBulkUpdateOpen(false);
    setBulkUpdateResults({ success: 0, failed: 0, errors: [] });
    setBulkUpdateProgress({ current: 0, total: 0 });
    setBulkUpdatePreview(null);
    setBulkUpdateStep('upload');
  };

  const handleMapModalOpen = () => {
    setMapModalOpen(true);
  };

  const handleMapModalClose = () => {
    setMapModalOpen(false);
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    
    // Validate required columns
    if (!headers.includes('deviceid')) {
      throw new Error('CSV file must contain a "deviceid" column');
    }

    // Find valid update columns (latitude, longitude, etc.)
    const validColumns = ['latitude', 'longitude', 'name', 'description', 'externalmeterid'];
    const updateColumns = headers.filter(header => validColumns.includes(header));
    
    if (updateColumns.length === 0) {
      throw new Error('CSV file must contain at least one valid column: ' + validColumns.join(', '));
    }

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(',').map(v => v.trim());
        const row = {};
        
        headers.forEach((header, index) => {
          if (updateColumns.includes(header) || header === 'deviceid') {
            row[header] = values[index] || '';
          }
        });
        
        if (row.deviceid) {
          data.push(row);
        }
      }
    }

    return { data, updateColumns };
  };

  const handleBulkUpdate = async (file) => {
    setBulkUpdateLoading(true);
    
    try {
      const text = await file.text();
      const { data, updateColumns } = parseCSV(text);
      
      // Prepare preview data and verify device existence
      const previewData = [];
      const verificationErrors = [];
      
      for (const row of data) {
        const updateData = {};
        updateColumns.forEach(col => {
          if (col !== 'deviceid' && row[col] !== undefined && row[col] !== '') {
            if (col === 'latitude' || col === 'longitude') {
              const num = parseFloat(row[col]);
              if (!isNaN(num)) {
                updateData[col] = num;
              }
            } else {
              updateData[col] = row[col];
            }
          }
        });
        
        if (Object.keys(updateData).length > 0) {
          // Verify device exists before adding to preview
          try {
            await axios.get(`${API_BASE_URL}/devices/${row.deviceid}`);
            previewData.push({
              deviceId: row.deviceid,
              updates: updateData
            });
          } catch (error) {
            if (error.response?.status === 404) {
              verificationErrors.push({
                deviceId: row.deviceid,
                error: 'Device not found'
              });
            } else {
              verificationErrors.push({
                deviceId: row.deviceid,
                error: error.response?.data?.error || error.message
              });
            }
          }
        }
      }
      
      setBulkUpdatePreview({
        totalDevices: previewData.length,
        updateColumns,
        previewData,
        verificationErrors
      });
      setBulkUpdateStep('preview');
      
    } catch (error) {
      setBulkUpdateResults({ 
        success: 0, 
        failed: 0, 
        errors: [{ deviceId: 'CSV Parse', error: error.message }] 
      });
    } finally {
      setBulkUpdateLoading(false);
    }
  };

  const handleBulkUpdateConfirm = async () => {
    if (!bulkUpdatePreview) return;
    
    setBulkUpdateStep('processing');
    setBulkUpdateLoading(true);
    setBulkUpdateResults({ success: 0, failed: 0, errors: [] });
    setBulkUpdateProgress({ current: 0, total: bulkUpdatePreview.previewData.length });
    
    let success = 0;
    let failed = 0;
    const errors = [];
    
    for (let i = 0; i < bulkUpdatePreview.previewData.length; i++) {
      const item = bulkUpdatePreview.previewData[i];
      setBulkUpdateProgress({ current: i + 1, total: bulkUpdatePreview.previewData.length });
      
      try {
        await axios.put(`${API_BASE_URL}/devices/${item.deviceId}`, item.updates);
        success++;
      } catch (error) {
        failed++;
        errors.push({
          deviceId: item.deviceId,
          error: error.response?.data?.error || error.message
        });
      }
    }
    
    setBulkUpdateResults({ success, failed, errors });
    
    // Refresh the data list to show updated information
    fetchData();
    setBulkUpdateLoading(false);
  };

  const handleBulkUpdateBack = () => {
    setBulkUpdateStep('upload');
    setBulkUpdatePreview(null);
  };

  // Get current data based on active section
  const getCurrentData = () => {
    return data;
  };

  // Get table headers based on active section
  const getTableHeaders = () => {
    switch (activeSection) {
      case 'users':
        return ['Name', 'Email'];
      case 'organizations':
        const orgHeaders = ['Name', 'Users', 'Devices'];
        // Add managers column for admin users
        if (hasAdminAccess()) {
          orgHeaders.push('Managers');
        }
        return orgHeaders;
      case 'devices':
        return ['Serial Number', 'External Meter ID', 'Name'];
      case 'groups':
        return ['Name', 'Description', 'Predefined'];
      default:
        return [];
    }
  };

  // Render cell value
  const renderCellValue = (row, field) => {
    const value = row[field];
    
    if (field === 'isactive' || field === 'isactivated' || field === 'isdefault' || field === 'predefined') {
      return (
        <Chip 
          label={value ? 'Active' : 'Inactive'} 
          color={value ? 'success' : 'error'} 
          size="small" 
        />
      );
    }
    
    if (field === 'defaultorganizationid') {
      return value ? 'Set' : 'Not Set';
    }
    
    return value || '-';
  };

  // Render managers column for organizations
  const renderManagersColumn = (organization) => {
    if (!hasAdminAccess()) {
      return '-';
    }

    const roles = organizationRoles[organization.id];
    if (!roles) {
      return 'Loading...';
    }

    const managers = [];
    
    // Add account managers
    if (roles.accountManagers && roles.accountManagers.length > 0) {
      roles.accountManagers.forEach(manager => {
        const name = manager.user?.firstname && manager.user?.lastname 
          ? `${manager.user.firstname} ${manager.user.lastname}`
          : manager.user?.email || 'Unknown User';
        managers.push(`${name} (AM)`);
      });
    }
    
    // Add support responsible
    if (roles.supportResponsible && roles.supportResponsible.length > 0) {
      roles.supportResponsible.forEach(support => {
        const name = support.user?.firstname && support.user?.lastname 
          ? `${support.user.firstname} ${support.user.lastname}`
          : support.user?.email || 'Unknown User';
        managers.push(`${name} (SR)`);
      });
    }

    if (managers.length === 0) {
      return 'No managers assigned';
    }

    return managers.join(', ');
  };

  // Handle role changes
  const handleRoleChange = async () => {
    // Refresh organization roles
    if (hasAdminAccess() && organizations.length > 0) {
      const orgIds = organizations.map(org => org.id);
      await fetchOrganizationRoles(orgIds);
    }
  };

  // Render user details
  const renderUserDetails = (user) => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <WalteroTypography variant="h6" gutterBottom>Personal Information</WalteroTypography>
            <Box sx={{ mb: 2 }}>
              <WalteroTypography variant="body2" color="textSecondary">Full Name</WalteroTypography>
              <WalteroTypography variant="body1">
                {user.firstname} {user.middlename} {user.lastname}
              </WalteroTypography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <WalteroTypography variant="body2" color="textSecondary">Email</WalteroTypography>
              <WalteroTypography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1, fontSize: 'small' }} />
                {user.email}
              </WalteroTypography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <WalteroTypography variant="h6" gutterBottom>Account Information</WalteroTypography>
            <Box sx={{ mb: 2 }}>
              <WalteroTypography variant="body2" color="textSecondary">Last Login</WalteroTypography>
              <WalteroTypography variant="body1">{formatLastLogin(user.lastlogin)}</WalteroTypography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      {user.organizations && user.organizations.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <WalteroTypography variant="h6" gutterBottom>Organizations</WalteroTypography>
              <Grid container spacing={2}>
                {user.organizations.map((org, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card 
                      variant="outlined"
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                      onClick={() => handleOrganizationClick(org)}
                    >
                      <CardContent>
                        <WalteroTypography variant="body1" fontWeight="bold">
                          {org.name}
                        </WalteroTypography>
                        <WalteroTypography variant="body2" color="textSecondary">
                          {org.group_name}
                        </WalteroTypography>
                        <WalteroTypography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                          Click to view details
                        </WalteroTypography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );

  // Organization Hierarchy Tree Component  
  const OrganizationTree = ({ node, selectedOrgId, level = 0, onOrganizationClick }) => {
    const isSelected = node.id === selectedOrgId;
    const isAncestor = node.relation_type === 'ancestor';
    const isDescendant = node.relation_type === 'descendant';
    
    const getNodeColor = () => {
      if (isSelected) return { bg: '#1976d2', text: '#fff', border: '#1976d2' };
      if (isAncestor) return { bg: '#f3e5f5', text: '#7b1fa2', border: '#ce93d8' };
      return { bg: '#e3f2fd', text: '#1565c0', border: '#90caf9' };
    };
    
    const colors = getNodeColor();
    
    return (
      <Box sx={{ width: '100%' }}>
        {/* Organization node */}
        <Box 
          sx={{ 
            ml: level * 4,
            mb: 2,
            maxWidth: `calc(100% - ${level * 4 * 8}px)`, // Prevent overflow
            position: 'relative'
          }}
        >
          {/* Simple connection line for children */}
          {level > 0 && (
            <Box 
              sx={{
                position: 'absolute',
                left: -16,
                top: 24,
                width: 16,
                height: 2,
                backgroundColor: '#bdbdbd'
              }}
            />
          )}
          
          <Card 
            elevation={isSelected ? 4 : 2}
            sx={{ 
              border: `2px solid ${colors.border}`,
              backgroundColor: colors.bg,
              borderRadius: 2,
              transition: 'all 0.2s ease-in-out',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: theme => theme.shadows[6],
                backgroundColor: isSelected ? colors.bg : '#f5f5f5'
              }
            }}
            onClick={() => onOrganizationClick && onOrganizationClick({
              id: node.id,
              name: node.name,
              organization_id: node.id,
              organization_name: node.name
            })}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {/* Icon and chip */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  {isSelected ? (
                    <BusinessIcon sx={{ color: colors.text, fontSize: 20 }} />
                  ) : (
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: colors.text,
                        opacity: 0.7
                      }} 
                    />
                  )}
                  
                  <Chip 
                    label={isSelected ? "Current" : isAncestor ? "Parent" : "Child"}
                    size="small"
                    sx={{
                      backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.8)',
                      color: colors.text,
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                      height: 20
                    }}
                  />
                </Box>
                
                {/* Organization name */}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <WalteroTypography 
                    variant="body1" 
                    sx={{
                      fontWeight: isSelected ? 700 : 600,
                      color: colors.text,
                      fontSize: isSelected ? '1.1rem' : '1rem',
                      wordBreak: 'break-word'
                    }}
                  >
                    {node.name}
                  </WalteroTypography>
                  {isSelected && (
                    <WalteroTypography 
                      variant="caption" 
                      sx={{ 
                        color: colors.text, 
                        opacity: 0.8,
                        fontStyle: 'italic',
                        display: 'block'
                      }}
                    >
                      You are viewing this organization
                    </WalteroTypography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        
        {/* Render children with proper spacing */}
        {node.children && node.children.length > 0 && (
          <Box sx={{ width: '100%' }}>
            {node.children.map((child) => (
              <OrganizationTree 
                key={child.id} 
                node={child} 
                selectedOrgId={selectedOrgId}
                level={level + 1}
                onOrganizationClick={onOrganizationClick}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // Render organization details
  const renderOrganizationDetails = (org) => (
    <Grid container spacing={3} sx={{ width: '100%' }}>
      {/* Organization Parent Card - EXACT copy of Users card */}
      {true && (
        <Grid item xs={12}>
          <Card sx={{ width: '100%', mb: 3 }}>
            <CardContent sx={{ width: '100%' }}>
              <WalteroTypography variant="h6" gutterBottom>Organization</WalteroTypography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card 
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        transform: 'translateY(-2px)',
                        boxShadow: 2
                      }
                    }}
                  >
                    <CardContent sx={{ py: 1, px: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <WalteroTypography variant="body2" fontWeight="bold">
                            {org.name}
                          </WalteroTypography>
                          <WalteroTypography variant="caption" color="textSecondary">
                            {org.description}
                          </WalteroTypography>
                        </Box>
                      </Box>
                      <Box sx={{ mt: 1 }}>
                        <WalteroTypography variant="caption" color="textSecondary">
                          Type: {org.organization_type_name}
                        </WalteroTypography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                {org.hierarchy && org.hierarchy.tree && org.hierarchy.tree.length > 0 && (
                  <Grid item xs={12}>
                    <Card 
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        }
                      }}
                    >
                      <CardContent sx={{ py: 1, px: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <WalteroTypography variant="body2" fontWeight="bold">
                              Organization Hierarchy
                            </WalteroTypography>
                            <WalteroTypography variant="caption" color="textSecondary">
                              Click organizations to navigate
                            </WalteroTypography>
                          </Box>
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          {org.hierarchy.tree.map((rootNode) => (
                            <OrganizationTree 
                              key={rootNode.id}
                              node={rootNode}
                              selectedOrgId={org.hierarchy.selectedOrgId}
                              onOrganizationClick={handleOrganizationClick}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Role Management Card - Admin Only */}
      {hasAdminAccess() && (
        <Grid item xs={12}>
          <Card sx={{ width: '100%', mb: 3 }}>
            <CardContent sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WalteroTypography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    Role Management
                  </WalteroTypography>
                  <Chip
                    label="Admin only"
                    size="small"
                    sx={{
                      bgcolor: '#c35d2c',
                      color: 'white',
                      fontSize: '0.7rem',
                      height: 20,
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                </Box>
                <WalteroButton
                  variant="outlined"
                  size="small"
                  onClick={() => setRoleManagementOpen(true)}
                >
                  Manage Roles
                </WalteroButton>
              </Box>
              <WalteroTypography variant="body2" color="textSecondary">
                Assign account managers and support responsible users for this organization.
              </WalteroTypography>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Devices Card - Full Width */}
      <Grid item xs={12}>
        <Card sx={{ width: '100%', mb: 3 }}>
          <CardContent sx={{ width: '100%' }}>
            <WalteroTypography variant="h6" gutterBottom>
              Devices ({orgDevices.length})
            </WalteroTypography>
            {loadingOrgDevices ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <WalteroDataTable
                data={orgDevices}
                columns={[
                  {
                    id: 'serialnumber',
                    label: 'Serial Number',
                    render: (value) => value || 'N/A'
                  },
                  {
                    id: 'externalmeterid',
                    label: 'External Meter ID',
                    render: (value) => (
                      <Box sx={{ fontFamily: 'monospace' }}>
                        {value || 'N/A'}
                      </Box>
                    )
                  },
                  {
                    id: 'name',
                    label: 'Name',
                    render: (value) => value || ''
                  }
                ]}
                onRowClick={(event, device) => {
                  // Close current modal
                  setModalOpen(false);
                  
                  // Switch to devices section
                  setActiveSection('devices');
                  setPage(0);
                  setSearchTerm('');
                  setSelectedOrganization('');
                  
                  // Wait for section switch, then open device modal
                  setTimeout(() => {
                    setSelectedItem(device);
                    setModalOpen(true);
                    fetchItemDetails(device.id, 'devices');
                  }, 100);
                }}
                pagination={{
                  defaultPageSize: 10
                }}
                ariaLabel="Organization devices table"
                emptyMessage="No devices found for this organization"
              />
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Users Parent Card - Keep original */}
      {org.users && org.users.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <WalteroTypography variant="h6" gutterBottom>Users with Access ({org.users.length})</WalteroTypography>
              <Grid container spacing={2}>
                {org.users.map((user, index) => (
                  <Grid item xs={12} sm={6} md={4} key={user.id}>
                    <Card 
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        }
                      }}
                      onClick={() => handleUserClick(user)}
                    >
                      <CardContent sx={{ py: 1, px: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <WalteroTypography variant="body2" fontWeight="bold">
                              {user.firstname} {user.lastname}
                            </WalteroTypography>
                            <WalteroTypography variant="caption" color="textSecondary">
                              {user.email}
                            </WalteroTypography>
                          </Box>
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          <WalteroTypography variant="caption" color="textSecondary">
                            Access through: {user.accessible_organizations?.map(org => `${org.name} (${org.group_name})`).join(', ')}
                          </WalteroTypography>
                          <WalteroTypography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                            Click to view details
                          </WalteroTypography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );

  // Device location map component with image gallery
  const DeviceMap = ({ device }) => {
    const [pictures, setPictures] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [loadingPictures, setLoadingPictures] = useState(false);
    const [showImageGallery, setShowImageGallery] = useState(false);

    const hasValidCoordinates = device.latitude && device.longitude && 
                               (device.latitude !== 0 || device.longitude !== 0);
    
    // Load pictures when component mounts
    useEffect(() => {
      const loadPictures = async () => {
        setLoadingPictures(true);
        try {
          const picturesData = await fetchDevicePictures(device.id);
          setPictures(picturesData.pictures || []);
        } catch (error) {
          console.error('Error loading device pictures:', error);
          setPictures([]);
        } finally {
          setLoadingPictures(false);
        }
      };

      if (device.id) {
        loadPictures();
      }
    }, [device.id]);

    const handleNextImage = () => {
      if (currentImageIndex < pictures.length - 1) {
        setCurrentImageIndex(prev => prev + 1);
      }
    };

    const handlePreviousImage = () => {
      if (currentImageIndex > 0) {
        setCurrentImageIndex(prev => prev - 1);
      }
    };

    if (!hasValidCoordinates) {
      return (
        <Card>
          <CardContent>
            <WalteroTypography variant="h6" gutterBottom>Location</WalteroTypography>
            <WalteroTypography variant="body2" color="textSecondary">
              No location data available for this device
            </WalteroTypography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent>
          <WalteroTypography variant="h6" gutterBottom>Device Location</WalteroTypography>
          <Box sx={{ height: 400, width: '100%', position: 'relative' }}>
            <MapContainer
              center={[device.latitude, device.longitude]}
              zoom={13}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <Marker position={[device.latitude, device.longitude]}>
                <Popup>
                  <div>
                    <strong>{device.name || `Device ${device.serialnumber}`}</strong><br />
                    Serial: {device.serialnumber}<br />
                    Status: {device.isactivated ? 'Activated' : 'Not Activated'}
                  </div>
                </Popup>
              </Marker>
            </MapContainer>
          </Box>
          <Box sx={{ mt: 2 }}>
            <WalteroTypography variant="body2" color="textSecondary">
              Coordinates: {device.latitude.toFixed(6)}, {device.longitude.toFixed(6)}
            </WalteroTypography>
          </Box>

          {/* Image Gallery Section */}
          <Box sx={{ mt: 2 }}>
            <WalteroTypography variant="h6" gutterBottom>Device Images</WalteroTypography>
            {loadingPictures && <WalteroTypography>Loading images...</WalteroTypography>}
            {!loadingPictures && pictures.length === 0 && (
              <WalteroTypography variant="body2" color="textSecondary">
                No images available for this device
              </WalteroTypography>
            )}
            {!loadingPictures && pictures.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <img
                    src={pictures[currentImageIndex].url}
                    alt={`Device ${device.serialnumber} - Image ${currentImageIndex + 1}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '300px',
                      height: 'auto',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <WalteroButton
                    onClick={handlePreviousImage}
                    disabled={currentImageIndex === 0}
                    startIcon={<ArrowBackIcon />}
                  >
                    Previous
                  </WalteroButton>
                  <WalteroTypography variant="body2">
                    {currentImageIndex + 1} of {pictures.length}
                  </WalteroTypography>
                  <WalteroButton
                    onClick={handleNextImage}
                    disabled={currentImageIndex >= pictures.length - 1}
                    endIcon={<ArrowForwardIcon />}
                  >
                    Next
                  </WalteroButton>
                </Box>
                <WalteroTypography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                  {new Date(pictures[currentImageIndex].lastModified).toLocaleString()}
                </WalteroTypography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Devices map component for showing all visible devices with image gallery
  const DevicesMap = ({ devices }) => {
    const [devicePictures, setDevicePictures] = useState({});
    const [currentImageIndex, setCurrentImageIndex] = useState({});
    const [loadingPictures, setLoadingPictures] = useState({});
    const [backgroundLoading, setBackgroundLoading] = useState(false);
    const [hasLoadedPictures, setHasLoadedPictures] = useState(false);

    const devicesWithCoordinates = devices.filter(device => 
      device.latitude && device.longitude && 
      (device.latitude !== 0 || device.longitude !== 0)
    );

    // Load all device pictures in background when component mounts
    useEffect(() => {
      const loadAllPictures = async () => {
        if (devicesWithCoordinates.length === 0 || hasLoadedPictures) return;
        
        console.log(`🔄 Starting background load of pictures for ${devicesWithCoordinates.length} devices`);
        setBackgroundLoading(true);
        
        // Load pictures for all devices in parallel
        const picturePromises = devicesWithCoordinates.map(async (device) => {
          try {
            console.log(`📸 Background loading pictures for device: ${device.id}`);
            const picturesData = await fetchDevicePictures(device.id);
            return { deviceId: device.id, pictures: picturesData.pictures };
          } catch (error) {
            console.error(`❌ Error background loading pictures for device ${device.id}:`, error);
            return { deviceId: device.id, pictures: [] };
          }
        });
        
        try {
          const results = await Promise.all(picturePromises);
          const newDevicePictures = {};
          results.forEach(result => {
            newDevicePictures[result.deviceId] = result.pictures;
          });
          
          setDevicePictures(newDevicePictures);
          setHasLoadedPictures(true);
          console.log(`✅ Background loading complete. Loaded pictures for ${Object.keys(newDevicePictures).length} devices`);
        } catch (error) {
          console.error('❌ Error in background loading:', error);
        } finally {
          setBackgroundLoading(false);
        }
      };
      
      loadAllPictures();
    }, [devicesWithCoordinates.length, hasLoadedPictures]); // Only depend on the length and flag

    // Fetch pictures for a device when popup opens (fallback for devices not loaded in background)
    const handlePopupOpen = async (deviceId) => {
      console.log(`🔍 handlePopupOpen called for device: ${deviceId}`);
      
      if (devicePictures[deviceId]) {
        console.log(`📸 Pictures already loaded for device: ${deviceId}`);
        return; // Already loaded
      }
      
      console.log(`📸 Loading pictures for device: ${deviceId}`);
      setLoadingPictures(prev => ({ ...prev, [deviceId]: true }));
      
      try {
        console.log(`🌐 Making API call to fetch pictures for device: ${deviceId}`);
        const picturesData = await fetchDevicePictures(deviceId);
        console.log(`✅ Pictures loaded for device ${deviceId}:`, picturesData);
        setDevicePictures(prev => ({ ...prev, [deviceId]: picturesData.pictures }));
        setCurrentImageIndex(prev => ({ ...prev, [deviceId]: 0 }));
      } catch (error) {
        console.error(`❌ Error loading pictures for device ${deviceId}:`, error);
        setDevicePictures(prev => ({ ...prev, [deviceId]: [] }));
      } finally {
        setLoadingPictures(prev => ({ ...prev, [deviceId]: false }));
      }
    };

    // Navigate to next image
    const handleNextImage = (deviceId) => {
      const pictures = devicePictures[deviceId] || [];
      const currentIndex = currentImageIndex[deviceId] || 0;
      if (currentIndex < pictures.length - 1) {
        setCurrentImageIndex(prev => ({ ...prev, [deviceId]: currentIndex + 1 }));
      }
    };

    // Navigate to previous image
    const handlePreviousImage = (deviceId) => {
      const currentIndex = currentImageIndex[deviceId] || 0;
      if (currentIndex > 0) {
        setCurrentImageIndex(prev => ({ ...prev, [deviceId]: currentIndex - 1 }));
      }
    };

    if (devicesWithCoordinates.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <WalteroTypography variant="h6" color="textSecondary">
            No devices with location data found in the current view
          </WalteroTypography>
        </Box>
      );
    }

    // Calculate center point for the map
    const centerLat = devicesWithCoordinates.reduce((sum, device) => sum + device.latitude, 0) / devicesWithCoordinates.length;
    const centerLng = devicesWithCoordinates.reduce((sum, device) => sum + device.longitude, 0) / devicesWithCoordinates.length;

    return (
      <Box sx={{ height: 600, width: '100%', position: 'relative' }}>
        <MapContainer
          center={[centerLat, centerLng]}
          zoom={10}
          style={{ height: '100%', width: '100%' }}
        >
          {/* Background loading indicator */}
          {backgroundLoading && (
            <div style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: '10px',
              borderRadius: '5px',
              zIndex: 1000,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              <WalteroTypography variant="body2" style={{ margin: 0 }}>
                🔄 Loading device images...
              </WalteroTypography>
            </div>
          )}
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {devicesWithCoordinates.map((device, index) => {
            const pictures = devicePictures[device.id] || [];
            const currentIndex = currentImageIndex[device.id] || 0;
            const currentPicture = pictures[currentIndex];
            const isLoading = loadingPictures[device.id] || false;
            
            // Determine marker color based on image availability
            const hasImages = pictures.length > 0;
            const markerIcon = hasImages ? greenPin : bluePin;
            console.log(`Device ${device.id}: hasImages=${hasImages}, pictures.length=${pictures.length}, using ${hasImages ? 'green' : 'blue'} pin`);
            
            return (
              <Marker 
                key={device.id || index} 
                position={[device.latitude, device.longitude]}
                icon={markerIcon}
                eventHandlers={{
                  popupopen: () => {
                    console.log(`🎯 Popup opened for device: ${device.id}`);
                    handlePopupOpen(device.id);
                  }
                }}
              >
                <Popup>
                  <div style={{ minWidth: '250px' }}>
                    <div>
                      <strong>{device.name || `Device ${device.serialnumber}`}</strong><br />
                      Serial: {device.serialnumber}<br />
                      External ID: {device.externalmeterid || 'N/A'}<br />
                      Status: {device.isactivated ? 'Activated' : 'Not Activated'}
                    </div>
                    
                    <hr style={{ margin: '10px 0' }} />
                    
                    {/* Image Gallery */}
                    <div style={{ minHeight: '150px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      {isLoading && <p>Loading images...</p>}
                      {!isLoading && pictures.length === 0 && <p>No images available</p>}
                      {!isLoading && pictures.length > 0 && currentPicture && (
                        <div style={{ textAlign: 'center' }}>
                          <img
                            src={currentPicture.url}
                            alt={`Device ${device.serialnumber} - Image ${currentIndex + 1}`}
                            style={{ 
                              maxWidth: '200px', 
                              maxHeight: '150px', 
                              height: 'auto', 
                              borderRadius: '4px',
                              border: '1px solid #ddd'
                            }}
                          />
                          <p style={{ fontSize: '0.8em', margin: '5px 0 0 0' }}>
                            {new Date(currentPicture.lastModified).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Navigation Controls */}
                    {pictures.length > 1 && (
                      <div style={{ 
                        marginTop: '10px', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center' 
                      }}>
                        <button 
                          onClick={() => handlePreviousImage(device.id)} 
                          disabled={currentIndex === 0}
                          style={{ 
                            fontSize: '1.2em', 
                            padding: '2px 8px',
                            cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
                            opacity: currentIndex === 0 ? 0.5 : 1
                          }}
                        >
                          &larr;
                        </button>
                        <span style={{ fontSize: '0.9em' }}>
                          {pictures.length > 0 ? `${currentIndex + 1} / ${pictures.length}` : '0 / 0'}
                        </span>
                        <button 
                          onClick={() => handleNextImage(device.id)} 
                          disabled={currentIndex >= pictures.length - 1}
                          style={{ 
                            fontSize: '1.2em', 
                            padding: '2px 8px',
                            cursor: currentIndex >= pictures.length - 1 ? 'not-allowed' : 'pointer',
                            opacity: currentIndex >= pictures.length - 1 ? 0.5 : 1
                          }}
                        >
                          &rarr;
                        </button>
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
        <Box sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'white', p: 1, borderRadius: 1, boxShadow: 2 }}>
          <WalteroTypography variant="caption" display="block">
            Showing {devicesWithCoordinates.length} of {devices.length} devices
          </WalteroTypography>
          {!backgroundLoading && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
              <WalteroTypography variant="caption" display="block" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <div style={{ 
                  width: '12px', 
                  height: '12px', 
                  backgroundColor: '#4CAF50', 
                  borderRadius: '50%', 
                  marginRight: '4px' 
                }}></div>
                Has images
              </WalteroTypography>
              <WalteroTypography variant="caption" display="block" sx={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ 
                  width: '12px', 
                  height: '12px', 
                  backgroundColor: '#2196F3', 
                  borderRadius: '50%', 
                  marginRight: '4px' 
                }}></div>
                No images
              </WalteroTypography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // Render device details
  const renderDeviceDetails = (device) => {
    if (editMode) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <WalteroTypography variant="h6" gutterBottom>Edit Device Information</WalteroTypography>
                <Box sx={{ mb: 2 }}>
                  <WalteroTypography variant="body2" color="textSecondary">Name</WalteroTypography>
                  <TextField
                    fullWidth
                    value={editData.name}
                    onChange={(e) => handleEditDataChange('name', e.target.value)}
                    size="small"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <WalteroTypography variant="body2" color="textSecondary">External Device ID</WalteroTypography>
                  <TextField
                    fullWidth
                    value={editData.externalmeterid || ''}
                    onChange={(e) => handleEditDataChange('externalmeterid', e.target.value)}
                    size="small"
                    fontFamily="monospace"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <WalteroTypography variant="body2" color="textSecondary">Description</WalteroTypography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={editData.description}
                    onChange={(e) => handleEditDataChange('description', e.target.value)}
                    size="small"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <WalteroTypography variant="body2" color="textSecondary">Serial Number (Read-only)</WalteroTypography>
                  <TextField
                    fullWidth
                    value={device.serialnumber}
                    disabled
                    size="small"
                  />
                </Box>
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <WalteroButton
                    variant="contained"
                    onClick={handleUpdateDevice}
                    disabled={updating}
                    startIcon={updating ? <CircularProgress size={20} /> : null}
                  >
                    {updating ? 'Updating...' : 'Save Changes'}
                  </WalteroButton>
                  <WalteroButton
                    variant="outlined"
                    onClick={handleCancelEdit}
                    disabled={updating}
                  >
                    Cancel
                  </WalteroButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <DeviceMap device={{ ...device, ...editData }} />
          </Grid>
        </Grid>
      );
    }

    return (
      <Box sx={{ width: '100%' }}>
        {/* Map at the top */}
        <Box sx={{ mb: 3 }}>
          <DeviceMap device={device} />
        </Box>
        
        {/* Tabs for different views */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={deviceTabValue} 
            onChange={handleDeviceTabChange}
            aria-label="device details tabs"
          >
            <Tab label="Device Info" />
            <Tab label="Performance" />
            <Tab label="Reports" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {deviceTabValue === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <WalteroTypography variant="h6">Device Information</WalteroTypography>
                    <WalteroButton
                      variant="outlined"
                      size="small"
                      onClick={handleEditMode}
                      startIcon={<SettingsIcon />}
                    >
                      Edit
                    </WalteroButton>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <WalteroTypography variant="body2" color="textSecondary">Name</WalteroTypography>
                    <WalteroTypography variant="body1">{device.name || 'N/A'}</WalteroTypography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <WalteroTypography variant="body2" color="textSecondary">External Device ID</WalteroTypography>
                    <WalteroTypography variant="body1" fontFamily="monospace">{device.externalmeterid || 'N/A'}</WalteroTypography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <WalteroTypography variant="body2" color="textSecondary">Serial Number</WalteroTypography>
                    <WalteroTypography variant="body1">{device.serialnumber}</WalteroTypography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <WalteroTypography variant="body2" color="textSecondary">Status</WalteroTypography>
                    <Chip 
                      label={device.isactivated ? 'Active' : 'Inactive'} 
                      color={device.isactivated ? 'success' : 'error'} 
                      size="small" 
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Users with Access Section */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <WalteroTypography variant="h6" gutterBottom>
                    Users with Access ({deviceUsers.length})
                  </WalteroTypography>
                  {loadingDeviceUsers ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                      <WalteroTypography variant="body2" sx={{ ml: 1 }}>Loading users...</WalteroTypography>
                    </Box>
                  ) : deviceUsers.length > 0 ? (
                    <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                      {deviceUsers.map((user, index) => (
                        <Box key={user.id} sx={{ mb: 2, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <WalteroTypography variant="body2" fontWeight="bold">
                                {user.firstname} {user.lastname}
                              </WalteroTypography>
                              <WalteroTypography variant="caption" color="textSecondary">
                                {user.email}
                              </WalteroTypography>
                            </Box>
                            <Chip 
                              label={user.isactive ? 'Active' : 'Inactive'} 
                              color={user.isactive ? 'success' : 'error'} 
                              size="small" 
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <WalteroTypography variant="body2" color="textSecondary">
                      No users found with access to this device.
                    </WalteroTypography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Performance Tab */}
        {deviceTabValue === 1 && (
          <Card>
            <CardContent>
              {/* Date Range Picker Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <WalteroTypography variant="h6">Device Performance</WalteroTypography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={devicePerformanceStartDate}
                    onChange={(e) => setDevicePerformanceStartDate(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={devicePerformanceEndDate}
                    onChange={(e) => setDevicePerformanceEndDate(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                  <WalteroButton
                    variant="contained"
                    size="small"
                    onClick={handleDevicePerformanceDateChange}
                    disabled={loadingDevicePerformance}
                  >
                    Reload
                  </WalteroButton>
                </Box>
              </Box>
              
              {loadingDevicePerformance ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                  <WalteroTypography variant="body2" sx={{ ml: 1 }}>Loading performance data...</WalteroTypography>
                </Box>
              ) : devicePerformanceData?.error ? (
                <Alert severity="error">{devicePerformanceData.error}</Alert>
              ) : devicePerformanceData ? (
                <Box>
                  {/* Summary Cards */}
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <WalteroTypography variant="h4" color="primary">
                          {devicePerformanceData.device?.successRate || 0}%
                        </WalteroTypography>
                        <WalteroTypography variant="body2" color="textSecondary">
                          Connectivity Rate
                        </WalteroTypography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <WalteroTypography variant="h4" color="secondary">
                          {devicePerformanceData.device?.rssiStats?.averageRSSI || 0}
                        </WalteroTypography>
                        <WalteroTypography variant="body2" color="textSecondary">
                          Avg RSSI ({devicePerformanceData.device?.rssiStats?.signalQuality || 'unknown'})
                        </WalteroTypography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <WalteroTypography variant="h4" color="info">
                          {devicePerformanceData.summary?.totalTransmissions || 0}
                        </WalteroTypography>
                        <WalteroTypography variant="body2" color="textSecondary">
                          Total Transmissions
                        </WalteroTypography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <WalteroTypography variant="h4" color="success">
                          {devicePerformanceData.device?.batteryStats?.averageVoltage || 0}V
                        </WalteroTypography>
                        <WalteroTypography variant="body2" color="textSecondary">
                          Avg Battery Voltage
                        </WalteroTypography>
                      </Paper>
                    </Grid>
                  </Grid>
                  
                  {/* Enhanced Device Performance Table and Histogram */}
                  <DevicePerformanceTable 
                    devicePerformanceData={devicePerformanceData}
                    startDate={devicePerformanceStartDate}
                    endDate={devicePerformanceEndDate}
                  />
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <WalteroTypography variant="body2" color="textSecondary" gutterBottom>
                    No performance data loaded. Click the button below to load data for the current date range.
                  </WalteroTypography>
                  <WalteroButton
                    variant="contained"
                    onClick={() => loadDevicePerformanceData(itemDetails?.id)}
                    disabled={!itemDetails?.id}
                  >
                    Load Performance Data
                  </WalteroButton>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Report Tab */}
        {deviceTabValue === 2 && (
          <Card>
            <CardContent>
              <WalteroTypography variant="h6" gutterBottom>Device Reports</WalteroTypography>
              {loadingDeviceReport ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                  <WalteroTypography variant="body2" sx={{ ml: 1 }}>Loading report data...</WalteroTypography>
                </Box>
              ) : deviceReportData?.error ? (
                <Alert severity="error">{deviceReportData.error}</Alert>
              ) : deviceReportData?.device ? (
                <Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <WalteroTypography variant="h4" color="primary">
                          {deviceReportData.summary?.totalUsage || 0}
                        </WalteroTypography>
                        <WalteroTypography variant="body2" color="textSecondary">
                          Total Usage
                        </WalteroTypography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <WalteroTypography variant="h4" color="secondary">
                          {deviceReportData.summary?.avgUsage || 0}
                        </WalteroTypography>
                        <WalteroTypography variant="body2" color="textSecondary">
                          Avg Daily Usage
                        </WalteroTypography>
                      </Paper>
                    </Grid>
                  </Grid>
                  <WalteroTypography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    Usage report data for the last month
                  </WalteroTypography>
                </Box>
              ) : (
                <WalteroTypography variant="body2" color="textSecondary">
                  Click to load report data for this device
                </WalteroTypography>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  // Render group details
  const renderGroupDetails = (group) => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <WalteroTypography variant="h6" gutterBottom>Role Information</WalteroTypography>
            <Box sx={{ mb: 2 }}>
              <WalteroTypography variant="body2" color="textSecondary">Name</WalteroTypography>
              <WalteroTypography variant="body1">{group.name}</WalteroTypography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <WalteroTypography variant="body2" color="textSecondary">Description</WalteroTypography>
              <WalteroTypography variant="body1">{group.description}</WalteroTypography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <WalteroTypography variant="body2" color="textSecondary">Default Role</WalteroTypography>
              <Chip 
                label={group.isdefault ? 'Yes' : 'No'} 
                color={group.isdefault ? 'success' : 'default'} 
                size="small" 
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <WalteroTypography variant="body2" color="textSecondary">Predefined</WalteroTypography>
              <Chip 
                label={group.predefined ? 'Yes' : 'No'} 
                color={group.predefined ? 'warning' : 'default'} 
                size="small" 
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      {group.organizations && group.organizations.length > 0 && (
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <WalteroTypography variant="h6" gutterBottom>Associated Organizations</WalteroTypography>
              <Grid container spacing={2}>
                {group.organizations.map((org, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card 
                      variant="outlined"
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                      onClick={() => handleOrganizationClick(org)}
                    >
                      <CardContent>
                        <WalteroTypography variant="body1" fontWeight="bold">
                          {org.name}
                        </WalteroTypography>
                        <WalteroTypography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                          Click to view details
                        </WalteroTypography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );

  // Effects
  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      fetchOrganizations();
    }
  }, [fetchStats, isAuthenticated]);

  // Fetch organization roles when organizations are loaded and user is on organizations section
  useEffect(() => {
    if (activeSection === 'organizations' && hasAdminAccess() && data.data && data.data.length > 0) {
      const orgIds = data.data.map(org => org.id);
      fetchOrganizationRoles(orgIds);
    }
  }, [activeSection, data, fetchOrganizationRoles]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [fetchData, isAuthenticated]);

  // Redirect away from admin-only tabs if user doesn't have access
  useEffect(() => {
    if ((activeSection === 'agent' || activeSection === 'api-docs' || activeSection === 'applications' || activeSection === 'subscriptions' || activeSection === 'api-usage') && !hasOrganizationAccess(ADMIN_ORGANIZATION_ID)) {
      setActiveSection('dashboard');
    }
  }, [activeSection]);

  // Load organizations when billing section is activated
  useEffect(() => {
    if (activeSection === 'billing' && isAuthenticated) {
      searchOrganizations('');
    }
  }, [activeSection, isAuthenticated, searchOrganizations]);

  const loadPerformanceData = async () => {
    if (!selectedOrganization) {
      setPerformanceError('Please select an organization.');
      return;
    }

    if (!performanceStartDate || !performanceEndDate) {
      setPerformanceError('Please select both Start Date and End Date.');
      return;
    }

    // Just set the data to trigger PerformanceTab rendering
    // The PerformanceTab component will handle its own API calls
    setPerformanceData({ devices: [], summary: null });
    setPerformanceError('');
  };

  const loadReportData = async () => {
    if (!selectedOrganization) {
      setReportError('Please select an organization.');
      return;
    }
    if (!reportStartDate || !reportEndDate) {
      setReportError('Please select both Start Date and End Date.');
      return;
    }
    // Just set the data to trigger ReportTab rendering
    // The ReportTab component will handle its own API calls and loading states
    setReportData({ devices: [], summary: null });
    setReportError('');
  };

  return (
    <WalteroThemeProvider>
      <Box sx={{ display: 'flex' }}>
        {/* Sidebar */}
        {isAuthenticated && (
          <Drawer
            variant="permanent"
            sx={{
              width: drawerOpen ? drawerWidth : drawerWidthClosed,
              flexShrink: 0,
              transition: 'width 0.3s ease',
              '& .MuiDrawer-paper': {
                width: drawerOpen ? drawerWidth : drawerWidthClosed,
                boxSizing: 'border-box',
                bgcolor: '#edefef',
                borderRight: '1px solid #d7e2e4',
                transition: 'width 0.3s ease',
                overflowX: 'hidden'
              },
            }}
          >
            <Toolbar />
            
            {/* Drawer Toggle Button */}
            <Box sx={{ display: 'flex', justifyContent: drawerOpen ? 'flex-end' : 'center', p: 1 }}>
              <Tooltip title={drawerOpen ? 'Collapse menu' : 'Expand menu'} placement="right">
                <IconButton 
                  onClick={handleDrawerToggle}
                  sx={{ 
                    backgroundColor: '#f18849',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#c35d2c'
                    },
                    width: 40,
                    height: 40
                  }}
                >
                  {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
              </Tooltip>
            </Box>
            
            <Box sx={{ overflow: 'auto', p: 0.25 }}>
              <List sx={{ display: 'flex', flexDirection: 'column' }}>
                {menuSections.map((section) => {
                  const sectionItems = section.items
                    .map((value) => menuItems.find((item) => item.value === value))
                    .filter(Boolean)
                    .filter((item) => {
                      if (['agent', 'api-docs', 'applications', 'subscriptions', 'api-usage'].includes(item.value) && !hasOrganizationAccess(ADMIN_ORGANIZATION_ID)) {
                        return false;
                      }
                      return true;
                    });

                  if (sectionItems.length === 0) {
                    return null;
                  }

                  return (
                    <React.Fragment key={section.title || 'primary'}>
                      {section.title && (
                        <Box sx={{ mt: section.title === 'Apps' || section.title === 'Management' || section.title === 'Support' ? 2.5 : 1.2, mb: 0.25, px: drawerOpen ? 1.2 : 0.6 }}>
                          {drawerOpen ? (
                            <WalteroTypography
                              variant="overline"
                              color="text.secondary"
                              sx={{
                                letterSpacing: '0.08em',
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                opacity: 0.7
                              }}
                            >
                              {section.title}
                            </WalteroTypography>
                          ) : (
                            <Divider sx={{ bgcolor: 'rgba(9, 72, 114, 0.15)' }} />
                          )}
                        </Box>
                      )}

                      {sectionItems.map((item) => (
                        <ListItem key={item.value} disablePadding>
                          <ListItemButton
                            selected={activeSection === item.value}
                            onClick={() => handleSectionChange(item.value)}
                            sx={{
                              borderRadius: 2,
                              mb: 0,
                              backgroundColor: activeSection === item.value ? '#f18849' : 'transparent',
                              color: activeSection === item.value ? 'white' : '#094872',
                              minHeight: 20,
                              py: 0.25,
                              justifyContent: drawerOpen ? 'initial' : 'center',
                              px: drawerOpen ? 1 : 0.5,
                              '&:hover': {
                                backgroundColor: activeSection === item.value ? '#c35d2c' : '#f4994c20',
                              },
                              '&.Mui-selected': {
                                backgroundColor: '#f18849',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: '#c35d2c',
                                },
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: activeSection === item.value
                                  ? 'white'
                                  : (['agent', 'api-docs', 'applications', 'subscriptions', 'api-usage'].includes(item.value)
                                      ? '#f18849'
                                      : '#094872'),
                                minWidth: drawerOpen ? 34 : 'unset',
                                mr: 0,
                                justifyContent: drawerOpen ? 'flex-start' : 'center'
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            {drawerOpen && (
                              <ListItemText
                                primary={item.text}
                                primaryTypographyProps={{
                                  fontWeight: activeSection === item.value ? 600 : 400,
                                  fontFamily: '"Mozaic Geo", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                  fontSize: '1.05rem'
                                }}
                              />
                            )}
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </React.Fragment>
                  );
                })}
              </List>
            </Box>
          </Drawer>
        )}

        {/* Main content */}
        <Box 
          component="main" 
          sx={{ 
            position: 'fixed',
            left: isAuthenticated ? (drawerOpen ? `${drawerWidth}px` : `${drawerWidthClosed}px`) : 0,
            right: 0,
            top: 0,
            bottom: 0,
            bgcolor: isAuthenticated ? '#fdf3eb' : 'transparent',
            overflow: 'auto',
            boxSizing: 'border-box',
            transition: 'left 0.3s ease'
          }}
        >
          {/* Show App Bar only when authenticated */}
          {isAuthenticated && (
            <>
              <AppBar 
                position="fixed" 
                sx={{ 
                  left: drawerOpen ? `${drawerWidth}px` : `${drawerWidthClosed}px`,
                  right: 0,
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                  bgcolor: '#023861',
                  color: 'white',
                  width: drawerOpen ? `calc(100vw - ${drawerWidth}px)` : `calc(100vw - ${drawerWidthClosed}px)`,
                  transition: 'left 0.3s ease, width 0.3s ease',
                  borderRadius: 0,
                  boxShadow: 'none',
                  borderBottom: '1px solid #094872'
                }}
              >
                <Toolbar>
                  <WalteroTypography 
                    variant="h6" 
                    component="div" 
                    sx={{ 
                      flexGrow: 1,
                      fontFamily: '"Mozaic Geo", "Helvetica Neue", Helvetica, Arial, sans-serif',
                      fontWeight: 'bold',
                      color: 'white'
                    }}
                  >
                    Mimir Control Center
                  </WalteroTypography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WalteroTypography 
                      variant="body2" 
                      sx={{ 
                        color: '#98c3cf',
                        fontFamily: '"Mozaic Geo", "Helvetica Neue", Helvetica, Arial, sans-serif'
                      }}
                    >
                      Welcome, {user?.firstName} {user?.lastName}
                    </WalteroTypography>
                    <WalteroButton 
                      sx={{
                        color: 'white',
                        backgroundColor: '#f18849',
                        fontFamily: '"Mozaic Geo", "Helvetica Neue", Helvetica, Arial, sans-serif',
                        '&:hover': {
                          backgroundColor: '#c35d2c',
                        },
                      }}
                      onClick={handleLogout}
                      startIcon={<LogoutIcon />}
                    >
                      Logout
                    </WalteroButton>
                  </Box>
                </Toolbar>
              </AppBar>
              <Toolbar />
            </>
          )}

          {!isAuthenticated ? (
            <WalteroLandingPage
              onLogin={handleLogin}
              loginLoading={loginLoading}
              loginError={loginError}
            />
          ) : (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                {/* Dashboard Overview */}
                {activeSection === 'dashboard' && (
              <Box>
                <WalteroTypography variant="h4" gutterBottom>
                  Dashboard Overview
                </WalteroTypography>
                
                {/* Statistics Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent>
                        <WalteroTypography color="textSecondary" gutterBottom>
                          Active Users
                        </WalteroTypography>
                        <WalteroTypography variant="h4">
                          {stats.active_users || 0}
                        </WalteroTypography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent>
                        <WalteroTypography color="textSecondary" gutterBottom>
                          Active Organizations
                        </WalteroTypography>
                        <WalteroTypography variant="h4">
                          {stats.active_organizations || 0}
                        </WalteroTypography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent>
                        <WalteroTypography color="textSecondary" gutterBottom>
                          Active Devices
                        </WalteroTypography>
                        <WalteroTypography variant="h4">
                          {stats.active_devices || 0}
                        </WalteroTypography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent>
                        <WalteroTypography color="textSecondary" gutterBottom>
                          Total Roles
                        </WalteroTypography>
                        <WalteroTypography variant="h4">
                          {stats.total_groups || 0}
                        </WalteroTypography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Performance Dashboard for Assigned Organizations - Only show if user has management roles */}
                {hasAdminAccess() && (
                  <Box sx={{ mb: 4 }}>
                    <PerformanceDashboard />
                  </Box>
                )}

                <WalteroTypography variant="h6" gutterBottom>
                  Quick Navigation
                </WalteroTypography>
                <Grid container spacing={2}>
                  {menuItems.slice(1)
                    .filter(item => !['agent', 'api-docs', 'applications', 'subscriptions'].includes(item.value) || hasOrganizationAccess(ADMIN_ORGANIZATION_ID))
                    .map((item) => (
                    <Grid item xs={12} sm={6} md={3} key={item.value}>
                      <Card 
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleSectionChange(item.value)}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {item.icon}
                            <WalteroTypography variant="h6" sx={{ ml: 1 }}>
                              {item.text}
                            </WalteroTypography>
                          </Box>
                          <WalteroTypography variant="body2" color="textSecondary">
                            Manage {item.text.toLowerCase()}
                          </WalteroTypography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Fleet Section */}
            {activeSection === 'performance' && (
              <Box>
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 300 }}
                    options={organizationOptions}
                    getOptionLabel={(option) => option.name || ''}
                    value={organizationOptions.find(org => org.id === selectedOrganization) || null}
                    onChange={(event, newValue) => {
                      setSelectedOrganization(newValue ? newValue.id : '');
                    }}
                    onOpen={() => {
                      // Load all organizations when dropdown opens
                      searchOrganizations('');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setOrganizationSearchTerm(newInputValue);
                      if (newInputValue.length >= 2) {
                        searchOrganizations(newInputValue);
                      } else if (newInputValue.length === 0) {
                        // Load all organizations when search is cleared
                        searchOrganizations('');
                      } else {
                        setOrganizationOptions([]);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Organization"
                        placeholder="Search organizations..."
                      />
                    )}
                    noOptionsText="No organizations found"
                    loading={false}
                    filterOptions={(x) => x}
                  />
                  <TextField
                    label="Start Date"
                    type="date"
                    variant="outlined"
                    size="small"
                    value={performanceStartDate}
                    onChange={(e) => setPerformanceStartDate(e.target.value)}
                    sx={{ minWidth: 150 }}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    variant="outlined"
                    size="small"
                    value={performanceEndDate}
                    onChange={(e) => setPerformanceEndDate(e.target.value)}
                    sx={{ minWidth: 150 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      onClick={() => {
                        const dates = getLastMonthDates();
                        setPerformanceStartDate(dates.startDate);
                        setPerformanceEndDate(dates.endDate);
                        if (selectedOrganization) {
                          setTimeout(() => loadPerformanceData(), 100);
                        }
                      }}
                      sx={{ 
                        minWidth: 'auto', 
                        fontSize: '0.75rem'
                      }}
                    >
                      Last Month
                    </WalteroButton>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      onClick={() => {
                        const dates = getThisMonthDates();
                        setPerformanceStartDate(dates.startDate);
                        setPerformanceEndDate(dates.endDate);
                        if (selectedOrganization) {
                          setTimeout(() => loadPerformanceData(), 100);
                        }
                      }}
                      sx={{ 
                        minWidth: 'auto', 
                        fontSize: '0.75rem'
                      }}
                    >
                      This Month
                    </WalteroButton>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      onClick={() => {
                        const dates = getOneMonthDates();
                        setPerformanceStartDate(dates.startDate);
                        setPerformanceEndDate(dates.endDate);
                        if (selectedOrganization) {
                          setTimeout(() => loadPerformanceData(), 100);
                        }
                      }}
                      sx={{ 
                        minWidth: 'auto', 
                        fontSize: '0.75rem'
                      }}
                    >
                      1 Month
                    </WalteroButton>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      onClick={() => {
                        const dates = getOneWeekDates();
                        setPerformanceStartDate(dates.startDate);
                        setPerformanceEndDate(dates.endDate);
                        if (selectedOrganization) {
                          setTimeout(() => loadPerformanceData(), 100);
                        }
                      }}
                      sx={{ 
                        minWidth: 'auto', 
                        fontSize: '0.75rem'
                      }}
                    >
                      1 Week
                    </WalteroButton>
                  </Box>
                  <WalteroButton
                    variant="contained"
                    onClick={loadPerformanceData}
                    disabled={performanceLoading || !selectedOrganization}
                  >
                    {performanceLoading ? 'Loading...' : 'Load Fleet Data'}
                  </WalteroButton>
                </Box>

                {!selectedOrganization && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Please select an organization to view fleet performance data.
                  </Alert>
                )}

                {performanceError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {performanceError}
                  </Alert>
                )}

                {performanceData && selectedOrganization && (
                  <PerformanceTab 
                    selectedOrgId={selectedOrganization} 
                    startDate={performanceStartDate}
                    endDate={performanceEndDate}
                  />
                )}

              </Box>
            )}

            {/* Consumption Section */}
            {activeSection === 'report' && (
              <Box>

                {/* Organization and Date Selection */}
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 300 }}
                    options={organizationOptions}
                    getOptionLabel={(option) => option.name || ''}
                    value={organizationOptions.find(org => org.id === selectedOrganization) || null}
                    onChange={(event, newValue) => {
                      setSelectedOrganization(newValue ? newValue.id : '');
                    }}
                    onOpen={() => {
                      searchOrganizations('');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setOrganizationSearchTerm(newInputValue);
                      if (newInputValue.length >= 2) {
                        searchOrganizations(newInputValue);
                      } else if (newInputValue.length === 0) {
                        searchOrganizations('');
                      } else {
                        setOrganizationOptions([]);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Organization"
                        placeholder="Search organizations..."
                      />
                    )}
                    noOptionsText="No organizations found"
                    loading={false}
                    filterOptions={(x) => x}
                  />
                  <TextField
                    label="Start Date"
                    type="date"
                    variant="outlined"
                    size="small"
                    value={reportStartDate}
                    onChange={(e) => setReportStartDate(e.target.value)}
                    sx={{ minWidth: 150 }}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    variant="outlined"
                    size="small"
                    value={reportEndDate}
                    onChange={(e) => setReportEndDate(e.target.value)}
                    sx={{ minWidth: 150 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      onClick={() => {
                        const dates = getLastMonthDates();
                        setReportStartDate(dates.startDate);
                        setReportEndDate(dates.endDate);
                      }}
                      sx={{ 
                        minWidth: 'auto', 
                        fontSize: '0.75rem'
                      }}
                    >
                      Last Month
                    </WalteroButton>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      onClick={() => {
                        const dates = getThisMonthDates();
                        setReportStartDate(dates.startDate);
                        setReportEndDate(dates.endDate);
                      }}
                      sx={{ 
                        minWidth: 'auto', 
                        fontSize: '0.75rem'
                      }}
                    >
                      This Month
                    </WalteroButton>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      onClick={() => {
                        const dates = getOneMonthDates();
                        setReportStartDate(dates.startDate);
                        setReportEndDate(dates.endDate);
                      }}
                      sx={{ 
                        minWidth: 'auto', 
                        fontSize: '0.75rem'
                      }}
                    >
                      1 Month
                    </WalteroButton>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      onClick={() => {
                        const dates = getOneWeekDates();
                        setReportStartDate(dates.startDate);
                        setReportEndDate(dates.endDate);
                      }}
                      sx={{ 
                        minWidth: 'auto', 
                        fontSize: '0.75rem'
                      }}
                    >
                      1 Week
                    </WalteroButton>
                  </Box>
                  <WalteroButton
                    variant="contained"
                    onClick={loadReportData}
                    disabled={reportLoading || !selectedOrganization}
                  >
                    {reportLoading ? 'Loading...' : 'Load Report Data'}
                  </WalteroButton>
                </Box>

                {!selectedOrganization && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Please select an organization to view usage reports.
                  </Alert>
                )}

                {reportError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {reportError}
                  </Alert>
                )}

                {reportData && selectedOrganization && (
                  <ReportTab 
                    selectedOrgId={selectedOrganization} 
                    startDate={reportStartDate}
                    endDate={reportEndDate}
                  />
                )}

              </Box>
            )}

            {/* Vision Section */}
            {activeSection === 'vision' && (
              <Box>
                <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 300 }}
                    options={organizationOptions}
                    getOptionLabel={(option) => option.name || ''}
                    value={organizationOptions.find(org => org.id === selectedOrganization) || null}
                    onChange={(event, newValue) => {
                      setSelectedOrganization(newValue ? newValue.id : '');
                    }}
                    onOpen={() => {
                      searchOrganizations('');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setOrganizationSearchTerm(newInputValue);
                      if (newInputValue.length >= 2) {
                        searchOrganizations(newInputValue);
                      } else if (newInputValue.length === 0) {
                        searchOrganizations('');
                      } else {
                        setOrganizationOptions([]);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Organization"
                        placeholder="Search organizations..."
                      />
                    )}
                    noOptionsText="No organizations found"
                    loading={false}
                    filterOptions={(x) => x}
                  />
                  <TextField
                    label="Start Date"
                    type="date"
                    variant="outlined"
                    size="small"
                    value={visionStartDate}
                    onChange={(e) => setVisionStartDate(e.target.value)}
                    sx={{ minWidth: 150 }}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    variant="outlined"
                    size="small"
                    value={visionEndDate}
                    onChange={(e) => setVisionEndDate(e.target.value)}
                    sx={{ minWidth: 150 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      onClick={() => {
                        const dates = getLastMonthDates();
                        setVisionStartDate(dates.startDate);
                        setVisionEndDate(dates.endDate);
                      }}
                      sx={{ minWidth: 'auto', fontSize: '0.75rem' }}
                    >
                      Last Month
                    </WalteroButton>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      onClick={() => {
                        const dates = getThisMonthDates();
                        setVisionStartDate(dates.startDate);
                        setVisionEndDate(dates.endDate);
                      }}
                      sx={{ minWidth: 'auto', fontSize: '0.75rem' }}
                    >
                      This Month
                    </WalteroButton>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      onClick={() => {
                        const dates = getOneMonthDates();
                        setVisionStartDate(dates.startDate);
                        setVisionEndDate(dates.endDate);
                      }}
                      sx={{ minWidth: 'auto', fontSize: '0.75rem' }}
                    >
                      1 Month
                    </WalteroButton>
                    <WalteroButton
                      walteroVariant="outlined"
                      size="small"
                      onClick={() => {
                        const dates = getOneWeekDates();
                        setVisionStartDate(dates.startDate);
                        setVisionEndDate(dates.endDate);
                      }}
                      sx={{ minWidth: 'auto', fontSize: '0.75rem' }}
                    >
                      1 Week
                    </WalteroButton>
                  </Box>
                  <WalteroButton
                    variant="contained"
                    onClick={() => setVisionRefreshKey((prev) => prev + 1)}
                    disabled={!selectedOrganization || !visionStartDate || !visionEndDate}
                  >
                    Load Vision Data
                  </WalteroButton>
                </Box>

                {!selectedOrganization && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Select an organization to explore its imagery.
                  </Alert>
                )}

                {selectedOrganization && visionRefreshKey === 0 && (
                  <WalteroCard variant="outlined" sx={{ p: 3 }}>
                    <WalteroTypography variant="body2" color="textSecondary">
                      Choose a date window and run Vision to pull the latest captures.
                    </WalteroTypography>
                  </WalteroCard>
                )}

                {selectedOrganization && visionRefreshKey > 0 && (
                  <VisionTab
                    selectedOrgId={selectedOrganization}
                    startDate={visionStartDate}
                    endDate={visionEndDate}
                    refreshKey={visionRefreshKey}
                  />
                )}
              </Box>
            )}

            {/* Cellular Section */}
            {activeSection === 'cellular' && (
              <Box>
                {/* Organization Selection */}
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 300 }}
                    options={organizationOptions}
                    getOptionLabel={(option) => option.name || ''}
                    value={organizationOptions.find(org => org.id === selectedOrganization) || null}
                    onChange={(event, newValue) => {
                      setSelectedOrganization(newValue ? newValue.id : '');
                    }}
                    onOpen={() => {
                      searchOrganizations('');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setOrganizationSearchTerm(newInputValue);
                      if (newInputValue.length >= 2) {
                        searchOrganizations(newInputValue);
                      } else if (newInputValue.length === 0) {
                        searchOrganizations('');
                      } else {
                        setOrganizationOptions([]);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Organization"
                        placeholder="Search organizations..."
                      />
                    )}
                    noOptionsText="No organizations found"
                    loading={false}
                    filterOptions={(x) => x}
                  />
                </Box>

                {!selectedOrganization && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Please select an organization to view cellular data.
                  </Alert>
                )}

                {selectedOrganization && selectedOrganization !== 'undefined' && selectedOrganization !== undefined && selectedOrganization !== '' && (
                  <CellularTab 
                    selectedOrgId={selectedOrganization} 
                  />
                )}
              </Box>
            )}

            {/* Telemetry Section */}
            {activeSection === 'telemetry' && (
              <Box>
                {/* Organization Selection */}
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 300 }}
                    options={organizationOptions}
                    getOptionLabel={(option) => option.name || ''}
                    value={organizationOptions.find(org => org.id === selectedOrganization) || null}
                    onChange={(event, newValue) => {
                      setSelectedOrganization(newValue ? newValue.id : '');
                    }}
                    onOpen={() => {
                      searchOrganizations('');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setOrganizationSearchTerm(newInputValue);
                      if (newInputValue.length >= 2) {
                        searchOrganizations(newInputValue);
                      } else if (newInputValue.length === 0) {
                        searchOrganizations('');
                      } else {
                        setOrganizationOptions([]);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Organization"
                        placeholder="Search organizations..."
                      />
                    )}
                    noOptionsText="No organizations found"
                    loading={false}
                    filterOptions={(x) => x}
                  />
                </Box>

                {!selectedOrganization && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Please select an organization to view telemetry visualizations.
                  </Alert>
                )}

                {selectedOrganization && selectedOrganization !== 'undefined' && selectedOrganization !== undefined && selectedOrganization !== '' && (
                  <TelemetryTab 
                    selectedOrgId={selectedOrganization} 
                  />
                )}
              </Box>
            )}

            {/* Applications Management Section */}
            {activeSection === 'applications' && (
              <Box>
                <ApplicationsTab />
              </Box>
            )}

            {/* Subscription Management Section */}
            {activeSection === 'subscriptions' && (
              <Box>
                <SubscriptionTab />
              </Box>
            )}

            {/* Billing Section */}
            {activeSection === 'billing' && (
              <Box>
                {/* Organization Selection */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 300 }}
                    options={organizationOptions}
                    getOptionLabel={(option) => option.name || ''}
                    value={organizationOptions.find(org => org.id === selectedOrganization) || null}
                    onChange={(event, newValue) => {
                      setSelectedOrganization(newValue ? newValue.id : '');
                    }}
                    onOpen={() => {
                      searchOrganizations('');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setOrganizationSearchTerm(newInputValue);
                      if (newInputValue.length >= 2) {
                        searchOrganizations(newInputValue);
                      } else if (newInputValue.length === 0) {
                        searchOrganizations('');
                      } else {
                        setOrganizationOptions([]);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Organization"
                        placeholder="Search organizations..."
                      />
                    )}
                    noOptionsText="No organizations found"
                    loading={false}
                    filterOptions={(x) => x}
                  />
                </Box>

                {!selectedOrganization && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Please select an organization to view billing and subscriptions.
                  </Alert>
                )}

                {selectedOrganization && (
                  <BillingTab
                    organizationId={selectedOrganization}
                    organizations={organizationOptions}
                  />
                )}
              </Box>
            )}

            {/* Agent Section */}
            {activeSection === 'agent' && (
              <Box>
                <AgentTab />
              </Box>
            )}
            
            {/* API Documentation Section */}
            {activeSection === 'api-docs' && (
              <Box>
                <ApiDocumentation
                  user={user}
                  token={token}
                  organizations={organizations}
                />
              </Box>
            )}

            {/* Documentation Section */}
            {activeSection === 'documentation' && (
              <Box>
                <DocumentationTab />
              </Box>
            )}

            {/* API Usage Section */}
            {activeSection === 'api-usage' && (
              <Box>
                <ApiUsageTab />
              </Box>
            )}

            {/* Data Sections */}
            {activeSection !== 'dashboard' &&
             activeSection !== 'performance' &&
             activeSection !== 'report' &&
             activeSection !== 'vision' &&
             activeSection !== 'cellular' &&
             activeSection !== 'telemetry' &&
             activeSection !== 'applications' &&
             activeSection !== 'subscriptions' &&
             activeSection !== 'agent' &&
             activeSection !== 'api-docs' &&
             activeSection !== 'documentation' &&
             activeSection !== 'api-usage' && (
              <Box>
                <WalteroTypography variant="h4" gutterBottom>
                  {menuItems.find(item => item.value === activeSection)?.text}
                </WalteroTypography>

                {/* Search and Filters */}
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    label="Search"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearch}
                    sx={{ minWidth: 200 }}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                  
                  {(activeSection === 'users' || activeSection === 'devices') && (
                    <Autocomplete
                      size="small"
                      sx={{ minWidth: 300 }}
                      options={organizationOptions}
                      getOptionLabel={(option) => option.name || ''}
                      value={organizationOptions.find(org => org.id === selectedOrganization) || null}
                      onChange={(event, newValue) => {
                        setSelectedOrganization(newValue ? newValue.id : '');
                      }}
                      onOpen={() => {
                        // Load all organizations when dropdown opens
                        searchOrganizations('');
                      }}
                      onInputChange={(event, newInputValue) => {
                        setOrganizationSearchTerm(newInputValue);
                        if (newInputValue.length >= 2) {
                          searchOrganizations(newInputValue);
                        } else if (newInputValue.length === 0) {
                          // Load all organizations when search is cleared
                          searchOrganizations('');
                        } else {
                          setOrganizationOptions([]);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Filter by Organization"
                          placeholder="Search organizations..."
                        />
                      )}
                      noOptionsText="No organizations found"
                      loading={false}
                      filterOptions={(x) => x}
                    />
                  )}

                  {activeSection === 'devices' && (
                    <>
                      <WalteroButton
                        variant="outlined"
                        onClick={handleMapModalOpen}
                        startIcon={<LocationIcon />}
                        sx={{ mr: 2 }}
                      >
                        View on Map
                      </WalteroButton>
                      <WalteroButton
                        variant="contained"
                        onClick={handleBulkUpdateOpen}
                        startIcon={<SettingsIcon />}
                        sx={{ ml: 'auto' }}
                      >
                        Bulk Update Sensor Data
                      </WalteroButton>
                    </>
                  )}
                </Box>

                {/* Error Alert */}
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {/* Data Table */}
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)', minHeight: 400 }}>
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              {getTableHeaders().map((header, index) => (
                                <TableCell key={index}>{header}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getCurrentData().data.map((row, index) => (
                              <TableRow 
                                key={row.id || index}
                                hover
                                onClick={() => handleOpenModal(row)}
                                sx={{ cursor: 'pointer' }}
                              >
                                {activeSection === 'users' && (
                                  <>
                                    <TableCell>
                                      <WalteroTypography variant="body2" fontWeight="bold">
                                        {row.firstname} {row.lastname}
                                      </WalteroTypography>
                                    </TableCell>
                                    <TableCell>{row.email}</TableCell>
                                  </>
                                )}
                                {activeSection === 'organizations' && (
                                  <>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell align="center">{row.user_count || 0}</TableCell>
                                    <TableCell align="center">{row.device_count || 0}</TableCell>
                                    {hasAdminAccess() && (
                                      <TableCell>
                                        <WalteroTypography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                          {renderManagersColumn(row)}
                                        </WalteroTypography>
                                      </TableCell>
                                    )}
                                  </>
                                )}
                                {activeSection === 'devices' && (
                                  <>
                                    <TableCell>{row.serialnumber}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{row.externalmeterid}</TableCell>
                                    <TableCell>{row.name}</TableCell>
                                  </>
                                )}
                                {activeSection === 'groups' && (
                                  <>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{row.description}</TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={row.predefined ? 'Yes' : 'No'} 
                                        color={row.predefined ? 'warning' : 'default'} 
                                        size="small" 
                                      />
                                    </TableCell>
                                  </>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      <TablePagination
                        rowsPerPageOptions={[25, 50, 100, 200]}
                        component="div"
                        count={getCurrentData().pagination?.total || 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                      />
                    </>
                  )}
                </Paper>
              </Box>
            )}

            {/* Detail Modal */}
            <Dialog
              open={modalOpen}
              onClose={handleCloseModal}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <WalteroTypography variant="h6">
                    {activeSection === 'devices' && selectedItem?.serialnumber && selectedItem?.externalmeterid
                      ? `${selectedItem.serialnumber} - ${selectedItem.externalmeterid}`
                      : selectedItem?.name || selectedItem?.firstname || selectedItem?.id || 'Details'}
                  </WalteroTypography>
                  <IconButton onClick={handleCloseModal}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent>
                {loadingDetails ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : itemDetails ? (
                  <Box sx={{ width: '100%' }}>
                    {activeSection === 'users' && renderUserDetails(itemDetails)}
                    {activeSection === 'organizations' && renderOrganizationDetails(itemDetails)}
                    {activeSection === 'devices' && renderDeviceDetails(itemDetails)}
                    {activeSection === 'groups' && renderGroupDetails(itemDetails)}
                  </Box>
                ) : (
                  <WalteroTypography color="error">Failed to load details</WalteroTypography>
                )}
              </DialogContent>
              <DialogActions>
                <WalteroButton onClick={handleCloseModal}>Close</WalteroButton>
              </DialogActions>
            </Dialog>

            {/* Bulk Update Modal */}
            <Dialog
              open={bulkUpdateOpen}
              onClose={handleBulkUpdateClose}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <WalteroTypography variant="h6">
                    Bulk Update Sensor Data
                  </WalteroTypography>
                  <IconButton onClick={handleBulkUpdateClose}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent>
                {/* Upload Step */}
                {bulkUpdateStep === 'upload' && (
                  <Box sx={{ mb: 3 }}>
                    <WalteroTypography variant="body1" gutterBottom>
                      Upload a CSV file to bulk update device sensor data.
                    </WalteroTypography>
                    <WalteroTypography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      The CSV file must contain a <strong>deviceid</strong> column and at least one of the following columns:
                    </WalteroTypography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {['latitude', 'longitude', 'name', 'description', 'externalmeterid'].map(field => (
                        <Chip key={field} label={field} size="small" variant="outlined" />
                      ))}
                    </Box>
                    
                    <input
                      accept=".csv"
                      style={{ display: 'none' }}
                      id="csv-file-input"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleBulkUpdate(file);
                        }
                      }}
                    />
                    <label htmlFor="csv-file-input">
                      <WalteroButton
                        variant="outlined"
                        component="span"
                        disabled={bulkUpdateLoading}
                        startIcon={<SettingsIcon />}
                      >
                        {bulkUpdateLoading ? 'Processing...' : 'Choose CSV File'}
                      </WalteroButton>
                    </label>
                  </Box>
                )}

                {/* Preview Step */}
                {bulkUpdateStep === 'preview' && bulkUpdatePreview && (
                  <Box sx={{ mb: 3 }}>
                    <WalteroTypography variant="h6" gutterBottom>
                      Preview Updates
                    </WalteroTypography>
                    
                    {/* Show verification errors if any */}
                    {bulkUpdatePreview.verificationErrors && bulkUpdatePreview.verificationErrors.length > 0 && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <AlertTitle>Device Verification Issues</AlertTitle>
                        <WalteroTypography variant="body2" gutterBottom>
                          The following devices could not be verified and will be skipped:
                        </WalteroTypography>
                        <Box sx={{ maxHeight: 150, overflow: 'auto', mt: 1 }}>
                          {bulkUpdatePreview.verificationErrors.map((error, index) => (
                            <WalteroTypography key={index} variant="body2" color="error" sx={{ mb: 0.5 }}>
                              <strong>{error.deviceId}:</strong> {error.error}
                            </WalteroTypography>
                          ))}
                        </Box>
                      </Alert>
                    )}
                    
                    <WalteroTypography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      The following updates will be performed on {bulkUpdatePreview.totalDevices} devices:
                    </WalteroTypography>
                    
                    <Box sx={{ mb: 2 }}>
                      <WalteroTypography variant="body2" color="textSecondary" gutterBottom>
                        Fields to be updated:
                      </WalteroTypography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {bulkUpdatePreview.updateColumns.filter(col => col !== 'deviceid').map(field => (
                          <Chip key={field} label={field} size="small" color="primary" />
                        ))}
                      </Box>
                    </Box>

                    <WalteroTypography variant="body2" color="textSecondary" gutterBottom>
                      Sample updates (showing first 5 devices):
                    </WalteroTypography>
                    <Box sx={{ maxHeight: 300, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                      {bulkUpdatePreview.previewData.slice(0, 5).map((item, index) => (
                        <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <WalteroTypography variant="body2" fontWeight="bold" gutterBottom>
                            Device ID: {item.deviceId}
                          </WalteroTypography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {Object.entries(item.updates).map(([field, value]) => (
                              <WalteroTypography key={field} variant="body2" color="textSecondary">
                                {field}: {value}
                              </WalteroTypography>
                            ))}
                          </Box>
                        </Box>
                      ))}
                      {bulkUpdatePreview.previewData.length > 5 && (
                        <WalteroTypography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                          ... and {bulkUpdatePreview.previewData.length - 5} more devices
                        </WalteroTypography>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Processing Step */}
                {bulkUpdateStep === 'processing' && (
                  <Box sx={{ mb: 3 }}>
                    <WalteroTypography variant="h6" gutterBottom>
                      Processing Updates
                    </WalteroTypography>
                    <WalteroTypography variant="body2" gutterBottom>
                      Processing: {bulkUpdateProgress.current} / {bulkUpdateProgress.total}
                    </WalteroTypography>
                    <LinearProgress 
                      variant="determinate" 
                      value={(bulkUpdateProgress.current / bulkUpdateProgress.total) * 100} 
                      sx={{ mb: 2 }}
                    />
                  </Box>
                )}

                {/* Results */}
                {(bulkUpdateResults.success > 0 || bulkUpdateResults.failed > 0) && (
                  <Box sx={{ mb: 3 }}>
                    <WalteroTypography variant="h6" gutterBottom>
                      Update Results
                    </WalteroTypography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Chip 
                        label={`${bulkUpdateResults.success} Successful`} 
                        color="success" 
                        size="small" 
                      />
                      <Chip 
                        label={`${bulkUpdateResults.failed} Failed`} 
                        color="error" 
                        size="small" 
                      />
                    </Box>
                    
                    {bulkUpdateResults.errors.length > 0 && (
                      <Box>
                        <WalteroTypography variant="body2" color="textSecondary" gutterBottom>
                          Errors:
                        </WalteroTypography>
                        <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                          {bulkUpdateResults.errors.map((error, index) => (
                            <WalteroTypography key={index} variant="body2" color="error" sx={{ mb: 1 }}>
                              <strong>{error.deviceId}:</strong> {error.error}
                            </WalteroTypography>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                {bulkUpdateStep === 'preview' && (
                  <>
                    <WalteroButton onClick={handleBulkUpdateBack}>
                      Back
                    </WalteroButton>
                    <WalteroButton 
                      variant="contained" 
                      onClick={handleBulkUpdateConfirm}
                      disabled={bulkUpdateLoading}
                    >
                      Confirm Updates
                    </WalteroButton>
                  </>
                )}
                {bulkUpdateStep === 'upload' && (
                  <WalteroButton onClick={handleBulkUpdateClose}>
                    Close
                  </WalteroButton>
                )}
                {(bulkUpdateStep === 'processing' || (bulkUpdateResults.success > 0 || bulkUpdateResults.failed > 0)) && (
                  <WalteroButton onClick={handleBulkUpdateClose}>
                    Close
                  </WalteroButton>
                )}
              </DialogActions>
            </Dialog>

            {/* Map Modal */}
            <Dialog
              open={mapModalOpen}
              onClose={handleMapModalClose}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <WalteroTypography variant="h6">
                    Devices Map View
                  </WalteroTypography>
                  <IconButton onClick={handleMapModalClose}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent>
                <DevicesMap devices={getCurrentData().data} />
              </DialogContent>
              <DialogActions>
                <WalteroButton onClick={handleMapModalClose}>Close</WalteroButton>
              </DialogActions>
            </Dialog>

            {/* Role Management Modal */}
            {hasAdminAccess() && (
              <Dialog
                open={roleManagementOpen}
                onClose={() => setRoleManagementOpen(false)}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WalteroTypography variant="h6" sx={{ mb: 0 }}>
                        Organization Role Management
                      </WalteroTypography>
                      <Chip
                        label="Admin only"
                        size="small"
                        sx={{
                          bgcolor: '#c35d2c',
                          color: 'white',
                          fontSize: '0.7rem',
                          height: 20,
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    </Box>
                    <IconButton onClick={() => setRoleManagementOpen(false)}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </DialogTitle>
                <DialogContent>
                  {itemDetails && activeSection === 'organizations' ? (
                    <OrganizationRoleManagement
                      organization={itemDetails}
                      open={roleManagementOpen}
                      onClose={() => setRoleManagementOpen(false)}
                      onRoleChange={handleRoleChange}
                    />
                  ) : (
                    <WalteroTypography color="error">
                      No organization selected
                    </WalteroTypography>
                  )}
                </DialogContent>
              </Dialog>
            )}
            </Container>
          )}
        </Box>
      </Box>
    </WalteroThemeProvider>
  );
}

export default App;
